/**
 * Sistema de deduplicación de requests HTTP
 * Evita múltiples requests simultáneos a la misma URL
 * 
 * Basado en el análisis HAR que mostró:
 * - 5x requests duplicados a /usuarios/v1
 * - 5x requests duplicados a /api/docentes
 * - 4x requests duplicados a /api/talleres
 */

class RequestDeduplicator {
  constructor() {
    // Mapa de requests en vuelo: url -> Promise
    this.pendingRequests = new Map();
    
    // Cache de respuestas exitosas (opcional, para evitar requests muy cercanos)
    this.responseCache = new Map();
    this.cacheTTL = 1000; // 1 segundo de cache para respuestas
  }

  /**
   * Genera una clave única para un request basada en método, URL y parámetros
   */
  getRequestKey(config) {
    const method = (config.method || 'get').toLowerCase();
    const url = config.url || '';
    const params = config.params ? JSON.stringify(config.params) : '';
    const data = config.data ? JSON.stringify(config.data) : '';
    return `${method}:${url}:${params}:${data}`;
  }

  /**
   * Verifica si hay un request pendiente para esta clave
   */
  hasPendingRequest(key) {
    return this.pendingRequests.has(key);
  }

  /**
   * Obtiene el request pendiente
   */
  getPendingRequest(key) {
    return this.pendingRequests.get(key);
  }

  /**
   * Registra un nuevo request pendiente
   */
  setPendingRequest(key, promise) {
    this.pendingRequests.set(key, promise);
    
    // Limpiar cuando el request termine (éxito o error)
    promise
      .then(() => {
        this.pendingRequests.delete(key);
      })
      .catch(() => {
        this.pendingRequests.delete(key);
      });
  }

  /**
   * Verifica si hay una respuesta en cache válida
   */
  getCachedResponse(key) {
    const cached = this.responseCache.get(key);
    if (!cached) return null;
    
    const age = Date.now() - cached.timestamp;
    if (age > this.cacheTTL) {
      this.responseCache.delete(key);
      return null;
    }
    
    return cached.response;
  }

  /**
   * Guarda una respuesta en cache
   */
  setCachedResponse(key, response) {
    this.responseCache.set(key, {
      response: response,
      timestamp: Date.now()
    });
  }

  /**
   * Interceptor para axios que deduplica requests
   */
  createInterceptor() {
    return async (config) => {
      const key = this.getRequestKey(config);
      
      // Solo deduplicar requests GET (los POST/PUT/DELETE deben ejecutarse siempre)
      const isGet = (config.method || 'get').toLowerCase() === 'get';
      
      if (isGet) {
        // Verificar cache primero
        const cached = this.getCachedResponse(key);
        if (cached) {
          console.log(`[DEDUP] Cache hit: ${config.url}`);
          return Promise.reject({
            __fromCache: true,
            data: cached.data,
            status: cached.status,
            headers: cached.headers,
            config: cached.config
          });
        }
        
        // Verificar si hay un request pendiente
        if (this.hasPendingRequest(key)) {
          console.log(`[DEDUP] Reusing pending request: ${config.url}`);
          return this.getPendingRequest(key);
        }
      }
      
      // Crear nuevo request
      const requestPromise = this.executeRequest(config);
      
      if (isGet) {
        this.setPendingRequest(key, requestPromise);
        
        // Cachear respuesta exitosa
        requestPromise
          .then((response) => {
            this.setCachedResponse(key, response);
          })
          .catch(() => {
            // No cachear errores
          });
      }
      
      return requestPromise;
    };
  }

  /**
   * Ejecuta el request original (debe ser sobrescrito por el interceptor real)
   */
  async executeRequest(config) {
    // Esta función será reemplazada por el interceptor real de axios
    throw new Error('executeRequest debe ser implementado por el interceptor');
  }

  /**
   * Limpia el cache y requests pendientes
   */
  clear() {
    this.pendingRequests.clear();
    this.responseCache.clear();
  }

  /**
   * Limpia solo el cache de respuestas (mantiene requests pendientes)
   */
  clearCache() {
    this.responseCache.clear();
  }
}

// Instancia singleton
const requestDeduplicator = new RequestDeduplicator();

export default requestDeduplicator;

